// ----------------------------------------------------------------------------
// Section: Fabric Carousel
// ----------------------------------------------------------------------------
//
// Swiper-based fabric carousel with swatch selection and content switching
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { Swiper, Scrollbar, A11y, Keyboard, Navigation } from 'swiper';

// Swiper CSS
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/navigation';

// Video block functionality
import 'snippets/video-player/video-block';
import 'snippets/background-media/background-media';

// Styles
import './_fabric-carousel.scss';

// -------------------------------------
// Variables
// -------------------------------------

const SECTION_SELECTOR = '[data-section-id]';
const CAROUSEL_SELECTOR = '.swiper[data-fabric-carousel]';
const SWATCH_SELECTOR = '[data-fabric-select]';
const CONTENT_SELECTOR = '[data-fabric-content]';
const ACTIVE_CLASS = 'is-active';

// -------------------------------------
// Helpers
// -------------------------------------

const initFabricCarousel = (section) => {
    console.log('Initializing fabric carousel for section:', section);
    const carousel = section.querySelector(CAROUSEL_SELECTOR);
    console.log('Found carousel element:', carousel);

    if (!carousel) {
        console.error('Fabric carousel element not found:', CAROUSEL_SELECTOR);
        console.log('Available elements in section:', section.innerHTML);
        return;
    }

    const scrollbar = section.querySelector('.fabric-carousel__scrollbar');

    // Initialize Swiper with delay to ensure layout is ready
    setTimeout(() => {
        const swiper = new Swiper(carousel, {
        modules: [Scrollbar, A11y, Keyboard, Navigation],
        slidesPerView: 'auto',
        spaceBetween: 5,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
        a11y: false,
        slidesPerView: 3.9,
        watchOverflow: true,
        grabCursor: true,

        breakpoints: {
            1024: {
                slidesPerView: 5.3,
                spaceBetween: 25,
            }
        },
        scrollbar: {
            el: scrollbar,
            draggable: true,
        },
        navigation: {
            nextEl: section.querySelector('.fabric-carousel__nav--next'),
            prevEl: section.querySelector('.fabric-carousel__nav--prev'),
        }
    });


    // Handle swatch selection
    const swatches = section.querySelectorAll(SWATCH_SELECTOR);
    const contents = section.querySelectorAll(CONTENT_SELECTOR);

    swatches.forEach((swatch, index) => {
        swatch.addEventListener('click', (e) => {
            e.preventDefault();

            const fabricId = swatch.dataset.fabricSelect;
            const targetContent = section.querySelector(`[data-fabric-content="${fabricId}"]`);

            if (!targetContent) return;

            // Update active swatch
            swatches.forEach(s => {
                s.closest('.fabric-swatch').classList.remove(ACTIVE_CLASS);
                s.closest('.fabric-swatch').removeAttribute('data-active');
            });

            swatch.closest('.fabric-swatch').classList.add(ACTIVE_CLASS);
            swatch.closest('.fabric-swatch').setAttribute('data-active', 'true');

            // Update active content
            contents.forEach(content => {
                content.classList.remove(ACTIVE_CLASS);
                content.removeAttribute('data-active');
            });

            targetContent.classList.add(ACTIVE_CLASS);
            targetContent.setAttribute('data-active', 'true');

            // Slide to selected swatch
            swiper.slideTo(index);
        });

        // Handle focus for accessibility
        swatch.addEventListener('focus', () => {
            swiper.slideTo(index);
        });
    });

    // Handle anchor linking
    const handleAnchorLink = () => {
        const hash = window.location.hash.substring(1);
        if (!hash) return;

        // Check if hash matches section anchor
        const sectionAnchor = section.id;
        if (hash === sectionAnchor) {
            section.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // Check if hash matches a fabric ID
        const targetSwatch = section.querySelector(`[data-fabric-id="${hash}"]`);
        if (targetSwatch) {
            const swatchButton = targetSwatch.querySelector(SWATCH_SELECTOR);
            if (swatchButton) {
                // Scroll to section first
                section.scrollIntoView({ behavior: 'smooth' });

                // Then activate the fabric after a short delay
                setTimeout(() => {
                    swatchButton.click();
                }, 500);
            }
        }
    };

    // Handle anchor links on page load
    if (window.location.hash) {
        handleAnchorLink();
    }

        // Handle anchor links on hash change
        window.addEventListener('hashchange', handleAnchorLink);

        return swiper;
    }, 100); // 100ms delay
};

// -------------------------------------
// Events
// -------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM loaded, looking for fabric carousel sections...');
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    console.log('Found sections:', sections);

    sections.forEach(section => {
        console.log('Checking section:', section, 'Classes:', section.classList);
        // Look for fabric carousel by checking if it contains the carousel element
        const hasCarousel = section.querySelector(CAROUSEL_SELECTOR);
        if (hasCarousel) {
            console.log('Found fabric carousel section, initializing...');
            initFabricCarousel(section);
        }
    });
});

// Handle Shopify section events
document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section.classList.contains('page-section--fabric-carousel')) {
        initFabricCarousel(section);
    }
});

document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (section.classList.contains('page-section--fabric-carousel')) {
        // Cleanup if needed
        const swiper = section.querySelector('.swiper')?.swiper;
        if (swiper) {
            swiper.destroy(true, true);
        }
    }
});
